import { FetchError } from "@selector/shared";
import { type components, SyntenyTypeEnum } from "@selector/shared/openapi";
import type { RefObject } from "react";
import type { PanelGroupRef } from "widgets";
import type { StateCreator } from "zustand";

import {
  fetchComponentGenomes,
  fetchFeatureInfo,
  fetchFeaturesFamilyMembersOrThrow,
  fetchPangenomeAlignmentMembersByFeatureOrThrow,
  fetchPangenomeAlignmentMembersOrThrow,
  fetchSyntenyInfo,
} from "../../api/fetch.ts";
import { fetchSavedView, postSavedView, putSavedView } from "../../api/save/save-load-view.ts";
import { setupSyntenyWorkerMessageHandler } from "../../main-canvas/messages/handle-alignment-messages.ts";
import SyntenyWorker from "../../synteny-worker/index.ts?worker";
import type { TrackConfig } from "../../utils/tracks/create-tracks.ts";
import { getGenomicFeatureTypeFromTrackType, getSyntenyTypeBySequencingType } from "../../utils/type-conversion.ts";
import type { GenomeViewerStore } from "../store.ts";
export type SavedViewMetadata = Omit<components["schemas"]["CreateOrUpdateSyntenyViewRequest"], "state">;
export type SavedViewValidationData = Omit<components["schemas"]["CompatibilityResponse"], "state">;
export type SavedSyntenyView = components["schemas"]["StateRequest"];
export type LoadedSyntenyView = components["schemas"]["StateResponse"];

export interface SaveLoadState {
  viewMetadata: SavedViewMetadata | undefined;
  tableRequestBody: components["schemas"]["TableRequest"] | undefined;
  panelApiRef: RefObject<PanelGroupRef | null>;
}
export interface SaveLoadActions {
  getView(): SavedSyntenyView;
  setView(view: LoadedSyntenyView, syntenyId: number, onError: (error: Error | FetchError) => void): Promise<void>;
  saveView(metadata: SavedViewMetadata): Promise<number>;
  loadView(viewId: number, syntenyId: number, onError: (error: Error | FetchError) => void): Promise<void>;
  setTableRequestBody(requestBody: components["schemas"]["TableRequest"] | undefined): void;
}
export type SaveLoadSlice = SaveLoadActions & SaveLoadState;

export const initialSaveLoadState: SaveLoadState = {
  viewMetadata: undefined,
  tableRequestBody: undefined,
  panelApiRef: { current: null },
};

export const createSaveLoadSlice: StateCreator<GenomeViewerStore, [], [], SaveLoadSlice> = (set, get) => ({
  ...initialSaveLoadState,
  getView() {
    const {
      trackGroups,
      tracks,
      alignedFamily,
      activeMemberId,
      alignmentDensity,
      pileupRenderingMode,
      basePerPixel,
      scrollX,
      scrollY,
      syntenyOrganism,
      tableRequestBody,
      panelApiRef,
    } = get();

    const syntenyView = {
      trackGroups: [...trackGroups.values()].map((group) => ({
        organismId: group.organismId,
        contigId: group.contigId,
        groupId: group.groupId,
        graphics: group.graphics,
        trackIds: group.trackIds,
      })),
      tracks: [...tracks.values()].map((track) => ({
        width: track.width,
        height: track.height,
        text: track.text,
        color: track.color,
        synteny: track.synteny,
        baseStart: track.baseStart!,
        isVisible: track.isVisible,
        trackType: track.trackType,
        trackId: track.trackId,
        groupId: track.groupId,
        subTypeId: track.subType?.id,
        basePerPixel: track.basePerPixel ?? 0,
      })),
      alignedFamily,
      activeMemberId,
      alignmentDensity,
      pileupRenderingMode,
      basePerPixel,
      scrollX,
      scrollY,
      organismId: syntenyOrganism.id!,
      table: tableRequestBody,
      showTable: panelApiRef.current?.isOpen,
    };

    return syntenyView;
  },
  async setView(view, syntenyId, onError) {
    const { tracks, trackGroups, scrollX, scrollY, table, alignedFamily: loadedAlignedFamily, ...syntenyView } = view;
    try {
      const syntenyInfo = await fetchSyntenyInfo(syntenyId);
      const data = await fetchComponentGenomes(syntenyId);

      const alignedFamilyId = loadedAlignedFamily?.familyId;
      const alignedFamily = {
        ...loadedAlignedFamily,
        members: loadedAlignedFamily?.members.map((member) => ({ ...member, start: 0, stop: 0 })) ?? [],
      };

      if (alignedFamily.members.length > 0) {
        let familyMembers;
        let firstMemberStart: number | undefined;
        let firstMemberStop: number | undefined;
        if (alignedFamilyId) {
          const isGenomeAlignment =
            getSyntenyTypeBySequencingType(syntenyInfo.sequencingType) === SyntenyTypeEnum.GENOME_ALIGNMENT;
          familyMembers = isGenomeAlignment
            ? await fetchPangenomeAlignmentMembersOrThrow(syntenyId, alignedFamilyId)
            : await fetchFeaturesFamilyMembersOrThrow(alignedFamilyId, false);
        } else {
          // The active member is not part of the returned family members. We have to fetch the missing information.
          const firstMember = alignedFamily.members[0];
          const trackType = tracks.find((track) => track.trackId === firstMember.trackId)?.trackType;
          if (trackType) {
            const featureType = getGenomicFeatureTypeFromTrackType(trackType);
            const [fetchedMembers, featureInfo] = await Promise.all([
              fetchPangenomeAlignmentMembersByFeatureOrThrow(syntenyId, featureType, firstMember.featureId),
              fetchFeatureInfo(featureType, firstMember.featureId),
            ]);
            familyMembers = fetchedMembers;
            firstMemberStart = featureInfo.start ?? 0;
          }
        }
        if (familyMembers) {
          const memberLocationMap = new Map<number, { start: number; stop: number }>();
          for (const familyMember of familyMembers) {
            for (const contig of familyMember.contigs) {
              for (const member of contig.members) {
                if (member.locationId) {
                  memberLocationMap.set(member.locationId, { start: member.start ?? 0, stop: member.stop ?? 0 });
                }
              }
            }
          }
          const firstMemberFeatureId = alignedFamily.members[0]?.featureId;
          alignedFamily.members = alignedFamily.members.map((member) => {
            let start = memberLocationMap.get(member.featureId)?.start;
            if (start === undefined && member.featureId === firstMemberFeatureId && firstMemberStart !== undefined) {
              start = firstMemberStart;
            }

            let stop = memberLocationMap.get(member.featureId)?.stop;
            if (stop === undefined && member.featureId === firstMemberFeatureId && firstMemberStop !== undefined) {
              stop = firstMemberStop;
            }
            return {
              ...member,
              start: start ?? member.start,
              stop: stop ?? member.stop,
            };
          });
        }
      }

      const organismMap: GenomeViewerStore["organisms"] = new Map();

      const resurrectedTrackGroups = new Map(
        trackGroups.map(({ contigId, contigSequenceId, organismId, organismName, graphics, groupId, trackIds }) => [
          groupId,
          { organismId, organismName, contigId, contigName: contigSequenceId, groupId, graphics, trackIds },
        ]),
      );

      const storeUpdate: Partial<GenomeViewerStore> = {
        tracks: new Map(), // We populate the tracks later including imageBitmap and worker
        trackGroups: resurrectedTrackGroups,
        scrollX: 0,
        scrollY: 0,
        asynchronousScrolling: false,
        syntenyOrganism: { id: syntenyInfo.id, name: syntenyInfo.name, type: syntenyInfo.sequencingType },
        activeSyntenyType: getSyntenyTypeBySequencingType(syntenyInfo.sequencingType),
        tableRequestBody: table,
      };
      let trackConfigs: TrackConfig[] = [];

      // Process all organisms first
      for (const organism of data) {
        const { id, name, contigs } = organism;

        const contigsMap = new Map(contigs.map((contig) => [contig.id, contig]));

        organismMap.set(id, { id, name, contigs: contigsMap });

        // Store the state updates
        storeUpdate.organisms = organismMap;

        trackConfigs = tracks.map(
          ({ trackType, groupId, subType, color, text, isVisible, trackId }) =>
            ({
              trackType,
              group: groupId,
              options: {
                subType,
                color,
                text,
                isVisible,
                trackId,
              },
            }) as TrackConfig,
        );
      }

      if (!get().syntenyWorker) {
        storeUpdate.syntenyWorker = new SyntenyWorker();
      }

      if (view.showTable) {
        get().panelApiRef.current?.openPanel();
      }

      set({
        alignedFamily,
        ...storeUpdate,
        ...syntenyView,
        positionToNavigate: {
          x: scrollX,
          y: scrollY,
        },
      });
      get().createTracks(trackConfigs, onError);
      get().refreshTracks();
      get().refreshMain(); // because of new groups
      setupSyntenyWorkerMessageHandler(set, get);
    } catch (error) {
      if (error instanceof FetchError || error instanceof Error) {
        onError(error);
      }
    }

    get().refreshTracks();
    get().refreshMain();
  },
  async saveView(metadata) {
    const syntenyView = get().getView();

    const viewId: number = metadata.id
      ? await putSavedView(metadata, syntenyView) // Edit the view
      : await postSavedView(metadata, syntenyView);

    if (get().viewMetadata) {
      set({ viewMetadata: { ...metadata, id: viewId } });
    }
    return viewId;
  },
  async loadView(viewId, syntenyId, onError) {
    return fetchSavedView(viewId)
      .then((data) => {
        const view: LoadedSyntenyView = data.state;

        if (data.viewType !== "SYNTENY_VIEW") {
          get().loadTracks(syntenyId, [], onError);
          return;
        }

        if (view.organismId != syntenyId) {
          throw new Error("The view cannot be resurrected on this dataset.");
        }

        set({
          viewMetadata: {
            id: data.id,
            name: data.viewName,
            description: data.description,
            projectId: data.projectId,
            itemTagIds: data.itemTags?.map((tag) => tag.id) ?? [],
            path: data.path,
            shared: data.shared,
          },
        });

        return get().setView(view, syntenyId, onError);
      })
      .catch((error) => {
        onError(error);
      });
  },
  setTableRequestBody(tableRequestBody) {
    set({ tableRequestBody });
  },
});
